import React, { useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import './surveyform.scss';

export default function SurveyForm({
  title,
  description,
  questions,
  onSubmit,
  submitLabel = 'Submit',
  defaultValues: defaultValuesProp,
  normalizeEmptyToNull = true,
  submitOptionLabels = false,
  className = '',
}) {
  const defaultValues = useMemo(() => {
    if (defaultValuesProp) return defaultValuesProp;

    const dv = {};
    for (const q of questions) {
      dv[q.name] = q.type === 'multi' ? [] : '';
    }
    return dv;
  }, [questions, defaultValuesProp]);

  const {
    control,
    register,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
    getValues,
    watch,
  } = useForm({
    defaultValues,
    mode: 'onBlur',
    reValidateMode: 'onChange',
    shouldUnregister: true,
  });
  const watchedValues = watch();

  const submit = (data) => {
    // Optional cross-field hook via question config
    // If any question has { validateForm: (values) => ({ name, message } | null) }
    // we run them here. (PreSurvey uses its own cross-field check in its file.)
    for (const q of questions) {
      if (typeof q.validateForm === 'function') {
        const res = q.validateForm(getValues());
        if (res?.name && res?.message) {
          setError(res.name, { type: 'manual', message: res.message });
          return;
        }
      }
    }

    const mapSubmittedValue = (question, value) => {
      const shouldUseOptionLabels =
        question.submitOptionLabels ?? submitOptionLabels;

      if (!shouldUseOptionLabels || !Array.isArray(question.options)) {
        return value;
      }

      const getOptionLabel = (optionValue) => {
        const matchedOption = question.options.find(
          (option) => option.value === optionValue,
        );

        return matchedOption?.label ?? optionValue;
      };

      if (question.type === 'multi') {
        return Array.isArray(value) ? value.map(getOptionLabel) : value;
      }

      if (question.type === 'single' || question.type === 'select') {
        return getOptionLabel(value);
      }

      return value;
    };

    // Normalize
    if (!normalizeEmptyToNull) {
      const submittedData = {};
      for (const q of questions) {
        submittedData[q.name] = mapSubmittedValue(q, data[q.name]);
      }
      return onSubmit?.(submittedData);
    }

    const normalized = {};
    for (const q of questions) {
      const v = mapSubmittedValue(q, data[q.name]);

      if (q.type === 'multi') {
        normalized[q.name] = Array.isArray(v) && v.length ? v : null;
      } else {
        const s = String(v ?? '').trim();
        normalized[q.name] = s ? s : null;
      }
    }

    return onSubmit?.(normalized);
  };

  return (
    <div className={`svy ${className}`.trim()}>
      {(title || description) && (
        <div className='svy__intro'>
          {title ? <h2 className='svy__title'>{title}</h2> : null}
          {description ? <p className='svy__desc'>{description}</p> : null}
        </div>
      )}

      <form className='svy__form' onSubmit={handleSubmit(submit)} noValidate>
        {questions
          .filter(
            (q) =>
              typeof q.showWhen !== 'function' || q.showWhen(watchedValues),
          )
          .map((q) => (
            <SurveyQuestion
              key={q.name}
              q={q}
              control={control}
              register={register}
              error={errors?.[q.name]?.message}
              clearErrors={clearErrors}
            />
          ))}

        <div className='svy__actions'>
          <button
            type='submit'
            className='svy-btn svy-btn--primary'
            disabled={isSubmitting}
          >
            {submitLabel}
          </button>
        </div>
      </form>
    </div>
  );
}

function SurveyQuestion({ q, control, register, error, clearErrors }) {
  const requiredRule =
    q.required === true
      ? { required: q.requiredMessage || 'This question is required.' }
      : {};
  const accessibleLabel =
    q.ariaLabel || (typeof q.label === 'string' ? q.label : undefined);
  const questionClassName = q.numbered === false ? 'svy-q svy-q--unnumbered' : 'svy-q';

  return (
    <div className={questionClassName}>
      <div className='svy-q__header'>
        <div>
          <div className='svy-q__label'>
            {q.label}
            {q.required ? <span className='svy-q__req'> *</span> : null}
          </div>
          {q.hint ? <div className='svy-q__hint'>{q.hint}</div> : null}
        </div>
      </div>

      {/* SELECT (dropdown) */}
      {q.type === 'select' ? (
        <select className='svy-input' {...register(q.name, requiredRule)}>
          <option value=''>{q.placeholder || 'Select…'}</option>
          {(q.options || []).map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      ) : null}

      {/* ZIP (optional; validate only if filled) */}
      {q.type === 'zip' ? (
        <input
          className='svy-input'
          inputMode='numeric'
          autoComplete='postal-code'
          maxLength={5}
          placeholder={q.placeholder || 'e.g., 80202'}
          {...register(q.name, {
            ...requiredRule,
            validate: (val) => {
              const v = String(val || '').trim();
              if (!v) return true; // optional unless required
              return (
                /^\d{5}$/.test(v) || 'Please enter a valid 5-digit ZIP code.'
              );
            },
            onChange: (e) => {
              // keep digits only
              e.target.value = e.target.value.replace(/\D/g, '').slice(0, 5);
              if (q.clearErrorOnChange) clearErrors?.(q.name);
            },
          })}
        />
      ) : null}

      {/* SINGLE (radio group) */}
      {q.type === 'single' ? (
        <Controller
          control={control}
          name={q.name}
          rules={requiredRule}
          render={({ field }) => (
            <div
              className='svy-choices'
              role='radiogroup'
              aria-label={accessibleLabel}
            >
              {(q.options || []).map((opt) => (
                <label className='svy-choice' key={opt.value}>
                  <input
                    className='svy-choice__input'
                    type='radio'
                    name={field.name}
                    value={opt.value}
                    checked={field.value === opt.value}
                    onChange={() => field.onChange(opt.value)}
                    onBlur={field.onBlur}
                  />
                  <span className='svy-choice__text'>{opt.label}</span>
                </label>
              ))}
            </div>
          )}
        />
      ) : null}

      {/* MULTI (checkbox list) */}
      {q.type === 'multi' ? (
        <Controller
          control={control}
          name={q.name}
          rules={
            q.required
              ? {
                  validate: (arr) =>
                    (Array.isArray(arr) && arr.length > 0) ||
                    q.requiredMessage ||
                    'Select at least one option.',
                }
              : {}
          }
          render={({ field }) => {
            const selected = Array.isArray(field.value) ? field.value : [];
            const toggle = (val) => {
              if (selected.includes(val)) {
                field.onChange(selected.filter((v) => v !== val));
                return;
              }

              if (val === 'choose_not_to_answer') {
                field.onChange([val]);
                return;
              }

              field.onChange([
                ...selected.filter((v) => v !== 'choose_not_to_answer'),
                val,
              ]);
            };

            return (
              <div
                className='svy-choices'
                role='group'
                aria-label={accessibleLabel}
              >
                {(q.options || []).map((opt) => (
                  <label className='svy-choice' key={opt.value}>
                    <input
                      className='svy-choice__input'
                      type='checkbox'
                      checked={selected.includes(opt.value)}
                      onChange={() => toggle(opt.value)}
                      onBlur={field.onBlur}
                    />
                    <span className='svy-choice__text'>{opt.label}</span>
                  </label>
                ))}
              </div>
            );
          }}
        />
      ) : null}

      {/* TEXT */}
      {q.type === 'text' ? (
        <input
          className='svy-input'
          type='text'
          placeholder={q.placeholder || ''}
          {...register(q.name, requiredRule)}
        />
      ) : null}

      {/* TEXTAREA */}
      {q.type === 'textarea' ? (
        <textarea
          className='svy-textarea'
          rows={q.rows || 4}
          placeholder={q.placeholder || ''}
          {...register(q.name, requiredRule)}
        />
      ) : null}

      {error ? <div className='svy-error'>{error}</div> : null}
    </div>
  );
}
